/**
 * Code 128, juego B.
 *
 * POR QUE NO SE REUSA `barcode.ts`. Ese modulo hace EAN-13, EAN-8 y UPC-A, que
 * son simbologias de longitud fija y solo digitos: sirven para identificar un
 * PRODUCTO. El folio de una venta es un consecutivo que crece —hoy 47, en un
 * año 12 400— y ademas lleva prefijo de sucursal. En EAN no cabe, y forzarlo
 * dentro del rango interno 20-29 seria peor: chocaria con los codigos que la
 * tienda acuña para el granel y la caja acabaria abriendo un producto al
 * escanear un ticket.
 *
 * Code 128 es lo que usa cualquier punto de venta para esto: acepta cualquier
 * texto ASCII imprimible, de la longitud que sea, y todos los lectores lo leen
 * sin configurar nada.
 *
 * SOLO EL JUEGO B. Existen tres (A con caracteres de control, B con
 * minusculas, C que empaqueta pares de digitos y sale mas corto). Con juego B
 * solo, un folio de seis digitos ocupa unos 46 mm a magnificacion 1, que cabe
 * de sobra en un ticket. Implementar los tres para ahorrar un centimetro seria
 * triplicar el codigo que hay que revisar cuando algo no se deje escanear.
 */
/**
 * Los 107 simbolos, como anchos de sus seis elementos: barra, espacio, barra,
 * espacio, barra, espacio. El de parada lleva siete.
 *
 * Cada simbolo mide 11 modulos (el de parada, 13). `code128.test.ts` verifica
 * esa suma, que los 107 sean distintos y que un texto codificado se pueda
 * volver a leer: son las tres cosas que atrapan un error al transcribir la
 * tabla, y un error aqui produce una etiqueta que sencillamente no escanea.
 */
const PATTERNS = [
    '212222', '222122', '222221', '121223', '121322', '131222', '122213', '122312',
    '132212', '221213', '221312', '231212', '112232', '122132', '122231', '113222',
    '123122', '123221', '223211', '221132', '221231', '213212', '223112', '312131',
    '311222', '321122', '321221', '312212', '322112', '322211', '212123', '212321',
    '232121', '111323', '131123', '131321', '112313', '132113', '132311', '211313',
    '231113', '231311', '112133', '112331', '132131', '113123', '113321', '133121',
    '313121', '211331', '231131', '213113', '213311', '213131', '311123', '311321',
    '331121', '312113', '312311', '332111', '314111', '221411', '431111', '111224',
    '111422', '121124', '121421', '141122', '141221', '112214', '112412', '122114',
    '122411', '142112', '142211', '241211', '221114', '413111', '241112', '134111',
    '111242', '121142', '121241', '114212', '124112', '124211', '411212', '421112',
    '421211', '212141', '214121', '412121', '111143', '111341', '131141', '114113',
    '114311', '411113', '411311', '113141', '114131', '311141', '411131', '211412',
    '211214', '211232', '2331112',
];
/** Arranca el juego B. */
const START_B = 104;
const STOP = 106;
/** Primer y ultimo ASCII que el juego B sabe representar. */
const FIRST_CHAR = 32; // espacio
const LAST_CHAR = 126; // ~
/**
 * Modulos de silencio a cada lado.
 *
 * La norma pide diez modulos como minimo. Igual que en EAN, no son decoracion:
 * el lector los usa para saber donde empieza el simbolo, y recortarlos es la
 * causa mas comun de que un codigo impreso en casa no se deje leer.
 */
export const CODE128_QUIET_MODULES = 10;
/** True si el texto se puede representar completo en el juego B. */
export function isCode128B(value) {
    if (value.length === 0)
        return false;
    for (const char of value) {
        const code = char.codePointAt(0);
        if (code === undefined || code < FIRST_CHAR || code > LAST_CHAR)
            return false;
    }
    return true;
}
/**
 * Valores de los simbolos: inicio, datos, verificador y parada.
 *
 * El verificador es la suma del valor de inicio mas cada valor de dato por su
 * posicion (empezando en 1), modulo 103. Sin el, un lector puede confundir un
 * simbolo con otro cuando la impresion sale sucia y devolver un folio que si
 * existe pero no es el del ticket.
 */
export function code128Values(value) {
    if (!isCode128B(value)) {
        throw new Error(`"${value}" no se puede representar en Code 128 juego B`);
    }
    const data = [...value].map((char) => (char.codePointAt(0) ?? FIRST_CHAR) - FIRST_CHAR);
    let checksum = START_B;
    data.forEach((code, index) => {
        checksum += code * (index + 1);
    });
    return [START_B, ...data, checksum % 103, STOP];
}
/**
 * Convierte el texto en la cadena de modulos lista para dibujar.
 *
 * Los anchos de la tabla se alternan siempre empezando por barra, asi que el
 * primer numero son modulos negros, el segundo blancos, y asi.
 */
export function code128Pattern(value) {
    const modules = code128Values(value)
        .map((symbol) => {
        const widths = PATTERNS[symbol];
        if (widths === undefined)
            throw new Error(`Simbolo ${symbol} fuera de la tabla`);
        let out = '';
        for (let i = 0; i < widths.length; i++) {
            const width = Number(widths.charAt(i));
            out += (i % 2 === 0 ? '1' : '0').repeat(width);
        }
        return out;
    })
        .join('');
    return {
        modules,
        totalModules: modules.length + CODE128_QUIET_MODULES * 2,
        text: value,
    };
}
/**
 * Tabla expuesta solo para las pruebas.
 *
 * No se exporta desde el indice del paquete: nadie fuera de aqui tiene por que
 * dibujar Code 128 a mano.
 */
export const CODE128_PATTERNS_FOR_TESTS = PATTERNS;
/**
 * Folio de venta en texto, tal como va impreso y codificado.
 *
 * Lleva prefijo de sucursal porque el consecutivo es por tienda: dos sucursales
 * tienen su venta numero 47, y sin el prefijo un ticket escaneado en una
 * abriria la venta de la otra. Se rellena a seis digitos para que todos los
 * codigos midan igual y el hueco del ticket no cambie de tamaño con el tiempo.
 */
export function saleFolioCode(storeId, folio) {
    return `V${String(storeId).padStart(2, '0')}-${String(folio).padStart(6, '0')}`;
}
/** Lee un folio impreso. Devuelve null si el texto no tiene esa forma. */
export function parseSaleFolioCode(code) {
    const match = /^V(\d{2})-(\d{6})$/.exec(code.trim().toUpperCase());
    if (!match?.[1] || !match[2])
        return null;
    return { storeId: Number(match[1]), folio: Number(match[2]) };
}
//# sourceMappingURL=code128.js.map